import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {toAbsoluteUrl} from "../../../_metronic/helpers";
import {getBrandImage} from "../generic/OEMList";
import {useThemeMode, systemMode} from "../../../_metronic/partials/layout/theme-mode/ThemeModeProvider";
import axios from "axios";
import {ENTITY_ID_JOB_CARD, ENTITY_ID_ATTENDANCE} from "../generic/Constants";

export const BranchCard = ({branch, onBranchClick, preloadedStats}) => {
    const navigate = useNavigate();
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');
    const brandImage = getBrandImage(branch.dealerBrand);
    const brandImageUrl = toAbsoluteUrl(`/media/car/${brandImage}`);

    // Only apply minWidth on non-mobile screens (> 576px)
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 576);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Use preloaded stats if available, otherwise fetch individually (backward compatibility)
    useEffect(() => {
        if (preloadedStats) {
            setStats(preloadedStats);
            setStatsLoading(false);
        } else {
            fetchBranchStats();
        }
    }, [branch.id, preloadedStats]);

    const fetchBranchStats = () => {
        setStatsLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL;
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        Promise.all([
            axios.get(`${apiUrl}/api/generic/all`, {
                params: {
                    entityId: ENTITY_ID_JOB_CARD,
                    page: 1,
                    sizePerPage: 100,
                    currentIndex: 0,
                    fixedFilterColumn: 'branchId',
                    fixedFilterData: branch.id,
                    searchField: 'status',
                    searchString: 'OPEN'
                }
            }),
            axios.get(`${apiUrl}/api/generic/all`, {
                params: {
                    entityId: ENTITY_ID_JOB_CARD,
                    page: 1,
                    sizePerPage: 100,
                    currentIndex: 0,
                    fixedFilterColumn: 'branchId',
                    fixedFilterData: branch.id,
                    searchField: 'status',
                    searchString: 'IN_PROGRESS'
                }
            }),
            axios.get(`${apiUrl}/api/generic/all`, {
                params: {
                    entityId: ENTITY_ID_JOB_CARD,
                    page: 1,
                    sizePerPage: 100,
                    currentIndex: 0,
                    fixedFilterColumn: 'branchId',
                    fixedFilterData: branch.id,
                    searchField: 'status',
                    searchString: 'CLOSED'
                }
            }),
            axios.get(`${apiUrl}/api/generic/all`, {
                params: {
                    entityId: ENTITY_ID_ATTENDANCE,
                    page: 1,
                    sizePerPage: 200,
                    currentIndex: 0,
                    fixedFilterColumn: 'branchId',
                    fixedFilterData: branch.id
                }
            })
        ]).then(([openRes, inProgressRes, closedRes, attendanceRes]) => {
            const openJobs = openRes.data.entities || [];
            const inProgressJobs = inProgressRes.data.entities || [];
            const activeJobs = [...openJobs, ...inProgressJobs];

            let totalTreatments = 0;
            let completedTreatments = 0;
            activeJobs.forEach(job => {
                const treatments = job.selectedTreatments || [];
                totalTreatments += treatments.length;
                completedTreatments += treatments.filter(t => t.status === 'COMPLETE').length;
            });

            const treatmentProgress = totalTreatments > 0
                ? Math.round((completedTreatments / totalTreatments) * 100)
                : 0;

            const closedJobs = (closedRes.data.entities || []).filter(job => {
                const endDate = new Date(job.endDate || job.updatedOn);
                return endDate >= sevenDaysAgo;
            });

            const today = new Date();
            const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
            const todayAttendance = (attendanceRes.data.entities || []).filter(att => {
                const attDate = new Date(att.attendanceDate);
                return attDate >= todayStart && attDate <= todayEnd;
            });

            setStats({
                activeJobCardCount: activeJobs.length,
                totalTreatments,
                completedTreatments,
                treatmentProgress,
                closedLast7Days: closedJobs.length,
                todayAttendanceCount: todayAttendance.length
            });
            setStatsLoading(false);
        }).catch((error) => {
            console.error(`Error fetching stats for branch ${branch.id}`, error);
            setStats({
                activeJobCardCount: 0,
                totalTreatments: 0,
                completedTreatments: 0,
                treatmentProgress: 0,
                closedLast7Days: 0,
                todayAttendanceCount: 0
            });
            setStatsLoading(false);
        });
    };

    const handleBranchClick = (branch) => {
        // Use custom onClick handler if provided, otherwise use default navigation
        if (onBranchClick) {
            onBranchClick(branch);
        } else {
            navigate(`/jobCards/branch/${branch.id}`, {
                state: {
                    branchId: branch.id,
                    branchName: branch.name,
                    dealerName: branch.dealerName
                }
            });
        }
    };

    const activeJobCardCount = stats?.activeJobCardCount;
    const totalTreatments = stats?.totalTreatments || 0;
    const completedTreatments = stats?.completedTreatments || 0;
    const treatmentProgress = stats?.treatmentProgress || 0;
    const closedLast7Days = stats?.closedLast7Days;
    const todayAttendanceCount = stats?.todayAttendanceCount;

    const shimmer = (
        <div className="placeholder-glow">
            <span className="placeholder col-6 rounded"></span>
        </div>
    );

    return (
        <div className="col-md-6 col-lg-4 col-xl-3 mb-6" style={isMobile ? {} : { minWidth: '420px' }}>
            <div
                className="card h-100 border border-gray-300 hover-elevate-up cursor-pointer shadow-sm"
                onClick={() => handleBranchClick(branch)}
                style={{
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    backgroundColor: isDarkMode ? '#1e1e2d' : ''
                }}
            >
                <div className="card-body p-6 d-flex flex-column">
                    {/* Status Badge at Top Right */}
                    <div className="d-flex justify-content-end mb-1">
                        <span className="badge badge-light-success px-2 py-1 fs-9 fw-bolder">
                            ACTIVE
                        </span>
                    </div>

                    {/* Logo and Name Section */}
                    <div className="d-flex align-items-center mb-5">
                        <div
                            className="me-4 d-flex align-items-center justify-content-center bg-body border border-gray-300"
                            style={{
                                width: '50px',
                                height: '50px',
                                padding: '4px',
                                borderRadius: '8px',
                                flexShrink: 0
                            }}
                        >
                            <img
                                src={brandImageUrl}
                                alt={branch.dealerBrand}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                        <div className="d-flex flex-column">
                            <h3 className={`fs-5 fw-bolder ${isDarkMode ? 'text-white' : 'text-dark'} mb-0`}>
                                {branch.name}
                            </h3>
                            <div className="d-flex align-items-center">
                                <span className="text-muted fw-semibold fs-8">
                                    {branch.dealerName || 'No dealer assigned'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="separator separator-dashed mb-5"></div>

                    {/* Active Job Cards Info */}
                    <div className="mb-4">
                        <div className="d-flex align-items-center justify-content-between bg-light-primary rounded p-3">
                            <div className="d-flex align-items-center">
                                <i className="bi bi-clipboard-check fs-3 text-primary me-3"></i>
                                <div className="d-flex flex-column">
                                    <span className="text-muted fs-9 fw-bold text-uppercase">Active Jobs</span>
                                    <span className={`${isDarkMode ? 'text-white' : 'text-dark'} fs-6 fw-bolder`}>
                                        {statsLoading
                                            ? shimmer
                                            : `${activeJobCardCount} Job${activeJobCardCount !== 1 ? 's' : ''}`}
                                    </span>
                                </div>
                            </div>
                            {!statsLoading && activeJobCardCount > 0 && (
                                <span className="badge badge-primary fs-7 fw-bold px-3 py-2">
                                    {activeJobCardCount}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Treatment Progress Overview */}
                    {statsLoading ? (
                        <div className="mb-4">
                            <div className="placeholder-glow">
                                <span className="placeholder col-8 rounded mb-2"></span>
                                <span className="placeholder col-12 rounded" style={{height: '8px'}}></span>
                            </div>
                        </div>
                    ) : totalTreatments > 0 && (
                        <div className="mb-4">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-wrench-adjustable-circle text-info fs-6 me-2"></i>
                                    <span className={`${isDarkMode ? 'text-white' : 'text-dark'} fw-bold fs-8`}>
                                        {totalTreatments} Treatment{totalTreatments !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                <span className="text-muted fs-9 fw-bold">
                                    {completedTreatments}/{totalTreatments} Done
                                </span>
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="progress h-8px w-100 bg-secondary bg-opacity-25 rounded">
                                    <div
                                        className="progress-bar bg-success"
                                        role="progressbar"
                                        style={{width: `${treatmentProgress}%`}}
                                        title={`${treatmentProgress}% Complete`}
                                    ></div>
                                </div>
                                <span className={`ms-2 ${isDarkMode ? 'text-white' : 'text-dark'} fw-bold fs-8 text-nowrap`}>
                                    {treatmentProgress}%
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Completed & Attendance Side by Side */}
                    {statsLoading ? (
                        <div className="row g-3 mb-4">
                            <div className="col-6">
                                <div className="d-flex align-items-center bg-light-success rounded px-3 py-2 h-100 placeholder-glow">
                                    <span className="placeholder col-10 rounded"></span>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="d-flex align-items-center bg-light-info rounded px-3 py-2 h-100 placeholder-glow">
                                    <span className="placeholder col-10 rounded"></span>
                                </div>
                            </div>
                        </div>
                    ) : ((closedLast7Days !== undefined && closedLast7Days > 0) || todayAttendanceCount !== undefined) && (
                        <div className="row g-3 mb-4">
                            {/* Closed Jobs Last 7 Days */}
                            {closedLast7Days !== undefined && closedLast7Days > 0 && (
                                <div className={todayAttendanceCount !== undefined ? "col-6" : "col-12"}>
                                    <div className="d-flex align-items-center bg-light-success rounded px-3 py-2 h-100">
                                        <i className="bi bi-check-circle-fill text-success fs-5 me-2"></i>
                                        <div className="d-flex flex-column flex-grow-1">
                                            <span className="text-muted fs-9 fw-bold text-uppercase" style={{fontSize: '0.6rem'}}>Completed (7d)</span>
                                            <span className={`${isDarkMode ? 'text-white' : 'text-dark'} fs-7 fw-bolder`}>
                                                {closedLast7Days} Job{closedLast7Days !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Today's Attendance */}
                            {todayAttendanceCount !== undefined && (
                                <div className={(closedLast7Days !== undefined && closedLast7Days > 0) ? "col-6" : "col-12"}>
                                    <div className="d-flex align-items-center bg-light-info rounded px-3 py-2 h-100">
                                        <i className="bi bi-person-check-fill text-info fs-5 me-2"></i>
                                        <div className="d-flex flex-column flex-grow-1">
                                            <span className="text-muted fs-9 fw-bold text-uppercase" style={{fontSize: '0.6rem'}}>Attendance</span>
                                            <span className={`${isDarkMode ? 'text-white' : 'text-dark'} fs-7 fw-bolder`}>
                                                {todayAttendanceCount} Worker{todayAttendanceCount !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Roles Info - Side by Side */}
                    <div className="mt-auto">
                        <div className="row g-3">
                            {/* Supervisor */}
                            <div className="col-6">
                                <div className="d-flex align-items-center">
                                    <div className="symbol symbol-30px symbol-circle me-2 flex-shrink-0">
                                        <span className="symbol-label bg-light-primary text-primary fs-8 fw-bolder">
                                            S
                                        </span>
                                    </div>
                                    <div className="d-flex flex-column min-w-0">
                                        <span className="text-muted fs-9 fw-bold text-uppercase">Supervisors</span>
                                        <span className={`${isDarkMode ? 'text-white' : 'text-dark'} fs-8 fw-bold text-truncate`}>
                                            {branch.supervisorName && branch.supervisorName.length > 0
                                                ? branch.supervisorName.join(', ')
                                                : 'Not Assigned'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Manager */}
                            <div className="col-6">
                                <div className="d-flex align-items-center">
                                    <div className="symbol symbol-30px symbol-circle me-2 flex-shrink-0">
                                        <span className="symbol-label bg-light-warning text-warning fs-8 fw-bolder">
                                            M
                                        </span>
                                    </div>
                                    <div className="d-flex flex-column min-w-0">
                                        <span className="text-muted fs-9 fw-bold text-uppercase">Manager</span>
                                        <span className={`${isDarkMode ? 'text-white' : 'text-dark'} fs-8 fw-bold text-truncate`}>
                                            {branch.managerName || 'Not Assigned'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
